// ./src/components/features/SandboxFormPreview/SandboxFormPreview.tsx
import React, { useRef, useState } from "react";
import {
  Box,
  Paper,
  Group,
  Text,
  ActionIcon,
  Tooltip,
  SegmentedControl,
} from "@mantine/core";

import { AppIcon } from "@/components/generic";

const SCRIPTS_ROOT_URL =
  import.meta.env.VITE_SCRIPTS_ROOT_URL ||
  "https://api.formsubmits.com/api/scripts";

export interface SandboxFormPreviewProps {
  code: string;
  formName?: string;
  loading?: boolean;
  error?: string | null;
  theme?: string;
}

type DeviceMode = "desktop" | "tablet" | "mobile";

const DEVICE_WIDTHS: Record<DeviceMode, string> = {
  desktop: "100%",
  tablet: "768px",
  mobile: "375px",
};

// CDN links for theme libraries
const THEME_ASSETS: Record<string, { css?: string[]; js?: string[] }> = {
  default: {},
  bootstrap5: {
    css: [
      "https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css",
    ],
    js: [
      "https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/js/bootstrap.bundle.min.js",
    ],
  },
  tailwind: {
    js: ["https://cdn.tailwindcss.com"],
  },
  none: {},
};

export const SandboxFormPreview: React.FC<SandboxFormPreviewProps> = ({
  code,
  formName,
  loading = false,
  error = null,
  theme = "default",
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [deviceMode, setDeviceMode] = useState<DeviceMode>("desktop");
  const [iframeKey, setIframeKey] = useState(0);

  // Build theme CSS links
  const getThemeCssLinks = (): string => {
    const assets = THEME_ASSETS[theme] || {};
    const cssLinks = assets.css || [];
    return cssLinks
      .map((href) => `<link rel="stylesheet" href="${href}">`)
      .join("\n    ");
  };

  // Build theme JS scripts
  const getThemeJsScripts = (): string => {
    const assets = THEME_ASSETS[theme] || {};
    const jsScripts = assets.js || [];
    return jsScripts
      .map((src) => `<script src="${src}"></script>`)
      .join("\n  ");
  };

  // Build full HTML document for iframe
  const getIframeContent = (): string => {
    if (!code) return "";

    const themeCss = getThemeCssLinks();
    const themeJs = getThemeJsScripts();

    // Additional styles for Tailwind to ensure form inputs look good
    const tailwindFormStyles =
      theme === "tailwind"
        ? `
    <style>
      /* Tailwind form input fixes */
      input[type="text"],
      input[type="email"],
      input[type="tel"],
      input[type="url"],
      input[type="number"],
      input[type="password"],
      input[type="date"],
      input[type="time"],
      input[type="datetime-local"],
      input[type="file"],
      textarea,
      select {
        border: 1px solid #d1d5db;
        padding: 0.5rem 0.75rem;
      }
      input[type="text"]:focus,
      input[type="email"]:focus,
      input[type="tel"]:focus,
      input[type="url"]:focus,
      input[type="number"]:focus,
      input[type="password"]:focus,
      input[type="date"]:focus,
      input[type="time"]:focus,
      input[type="datetime-local"]:focus,
      input[type="file"]:focus,
      textarea:focus,
      select:focus {
        outline: none;
        border-color: #6366f1;
        box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
      }
    </style>
    `
        : "";

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  ${themeCss}
  ${themeJs}
  ${tailwindFormStyles}
  <style>
    * {
      box-sizing: border-box;
    }
    html, body {
      margin: 0;
      padding: 0;
      height: 100%;
    }
    body {
      padding: 1rem;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background-color: #ffffff;
    }
    form {
      max-width: 100%;
    }
  </style>
</head>
<body>
  ${code}

  <!-- FormSubmits AJAX Handler -->
  <script src="${SCRIPTS_ROOT_URL}/formsubmits-core.js"></script>
  <script>
    (function() {
      // Wait for DOM and script to be ready
      function initForm() {
        var form = document.querySelector('form');
        if (form && typeof FormSubmissionHandler === 'function') {
          var handler = new FormSubmissionHandler(form, {
            onSuccess: function(response) {
              console.log('Form submitted successfully:', response);
            },
            onError: function(response) {
              console.log('Form submission error:', response);
            }
          });

          // Enable auto-clear errors when user types
          handler.enableAutoClearErrors();

          // Intercept form submit to use AJAX
          form.addEventListener('submit', function(e) {
            e.preventDefault();
            var button = form.querySelector('button[type="submit"]');
            handler.submitAjax(button);
          });

          console.log('FormSubmissionHandler initialized');
        } else {
          console.log('Form or FormSubmissionHandler not found, retrying...');
          setTimeout(initForm, 100);
        }
      }

      // Start initialization
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initForm);
      } else {
        // Give time for the script to load
        setTimeout(initForm, 100);
      }
    })();
  </script>
</body>
</html>`;
  };

  // Refresh iframe
  const handleRefresh = () => {
    setIframeKey((prev) => prev + 1);
  };

  return (
    <Paper
      radius="md"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Group
        justify="space-between"
        px="md"
        py="xs"
        style={{
          borderBottom: "1px solid var(--mantine-color-gray-3)",
          backgroundColor: "var(--mantine-color-gray-0)",
          flexShrink: 0,
        }}
      >
        <Group gap="md">
          <Text size="sm" fw={500} c="dimmed">
            Preview
          </Text>
          {formName && (
            <Text size="sm" fw={500}>
              {formName}
            </Text>
          )}
        </Group>

        <Group gap="xs">
          <SegmentedControl
            size="xs"
            value={deviceMode}
            onChange={(value) => setDeviceMode(value as DeviceMode)}
            data={[
              {
                label: (
                  <Tooltip label="Desktop">
                    <Box>
                      <AppIcon iconName="DeviceDesktop" size={16} />
                    </Box>
                  </Tooltip>
                ),
                value: "desktop",
              },
              {
                label: (
                  <Tooltip label="Tablet">
                    <Box>
                      <AppIcon iconName="DeviceTablet" size={16} />
                    </Box>
                  </Tooltip>
                ),
                value: "tablet",
              },
              {
                label: (
                  <Tooltip label="Mobile">
                    <Box>
                      <AppIcon iconName="DeviceMobile" size={16} />
                    </Box>
                  </Tooltip>
                ),
                value: "mobile",
              },
            ]}
          />

          <Tooltip label="Refresh preview">
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={handleRefresh}
              disabled={loading || !code}
            >
              <AppIcon iconName="Refresh" size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {/* Content */}
      <Box
        style={{
          flex: 1,
          overflow: "auto",
          backgroundColor: "var(--mantine-color-gray-1)",
          display: "flex",
          justifyContent: "center",
          padding: deviceMode !== "desktop" ? "1rem" : 0,
          minHeight: 0,
        }}
      >
        {loading ? (
          <Box
            p="xl"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <Text c="dimmed" size="sm">
              Loading preview...
            </Text>
          </Box>
        ) : error ? (
          <Box
            p="xl"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <Text c="red" size="sm">
              {error}
            </Text>
          </Box>
        ) : !code ? (
          <Box
            p="xl"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <Text c="dimmed" size="sm">
              Select a form to see preview
            </Text>
          </Box>
        ) : (
          <Box
            style={{
              width: DEVICE_WIDTHS[deviceMode],
              maxWidth: "100%",
              height: "100%",
              backgroundColor: "#ffffff",
              boxShadow:
                deviceMode !== "desktop"
                  ? "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
                  : "none",
              borderRadius: deviceMode !== "desktop" ? "8px" : 0,
              overflow: "hidden",
              transition: "width 0.2s ease-in-out",
            }}
          >
            <iframe
              key={iframeKey}
              ref={iframeRef}
              srcDoc={getIframeContent()}
              title="Form Preview"
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                display: "block",
              }}
              sandbox="allow-scripts allow-forms allow-same-origin"
            />
          </Box>
        )}
      </Box>
    </Paper>
  );
};
